<template>
    <div class="modal" id="createUserModal" tabindex="-1" data-backdrop="static" data-keyboard="false">
        <div class="modal-dialog modal-dialog-centered modal-fullscreen-custom">
            <div class="modal-content">
                <div class="modal-header bg-white p-3">
                    <h5 class="modal-title text-teal font-medium">Create User</h5>
                    <button type="button" class="close" @click="close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body modal-scrollable">
                    <div class="flex justify-content-center">
                        <div class="stepper-wrapper">
                            <div v-for="step in steps" class="stepper-item"
                                 :class="[(currentStep > step.stepCount ? 'completed' : ''), (currentStep === step.stepCount ? 'active' : '')]">
                                <div class="step-counter"></div>
                                <div class="step-name">{{ step.name }}</div>
                            </div>
                        </div>
                        <div>
                            <span class="step-counter-text">Step {{ currentStep }} of {{ steps.length }}</span>
                        </div>
                    </div>
                    <div v-show="currentStep === 1" class="container py-5">
                        <!-- Section 1: Email Input -->
                        <div class="text-center mb-5">
                            <h4>Create a bunch of new users at the same time</h4>
                            <p class="text-muted">
                                Add a new user to your FollowupCRM account with an email address.
                            </p>

                            <div class="form-group w-50 mx-auto">
                                <label class="font-weight-bold" for="email">Add email address(es)</label>
                                <b-form-tags
                                    tag-pills
                                    input-id="tags-pills"
                                    tag-variant="custom"
                                    class="email-custom-tag-input"
                                    v-model="form.emails"
                                    :tag-validator="validateEmail"
                                    placeholder="Add one or multiple emails">
                                </b-form-tags>
                            </div>
                        </div>

                        <!-- Divider -->
                        <div class="section-divider"></div>

                        <!-- Section 2: CSV Upload -->
                        <div class="text-center">
                            <h4>Or create multiple users at once.</h4>
                            <p class="text-muted">
                                Create multiple users at once. Import their info from your integration app records.
                            </p>

                            <label for="csvFileInput" class="csv-upload-label mt-4">
                                <img
                                    src="https://img.icons8.com/ios/100/000000/document--v1.png"
                                    alt="CSV File"
                                />
                                <h6>A CSV file</h6>
                                <small class="text-muted d-block">
                                    Upload a CSV file or plain <br />
                                    text file in CSV format
                                </small>
                            </label>
                            <input
                                type="file"
                                id="csvFileInput"
                                class="csv-upload-input"
                                accept=".csv,text/csv"
                                @change="handleFileUpload"
                            />
                        </div>
                    </div>
                    <div v-show="currentStep === 2" class="row p-2">
                        <div class="col-12">
                            <choose-set-access
                                ref="ChooseSetAccess"
                                :team="team">
                            </choose-set-access>
                        </div>
                    </div>
                    <div v-show="currentStep === 3" class="row p-2">
                        <div class="container mt-3 text-center text-grey-darker">
                            <h2 class="mb-4 text-followup-nav">Review user access</h2>
                            <p class="mb-4">You’re almost done. Check all the access settings to make sure everything looks good.</p>

                            <!-- Emails List -->
                            <h4 class="mb-4 text-followup-nav">Users</h4>
                            <span v-if="form.emails.length > 1" class="mb-4">Teams can only be added when you invite one user at a time.</span>
                            <div
                                v-for="email in form.emails"
                                class="mb-2 d-flex align-items-center justify-content-center">
                                <img class="user-icon" src="https://www.gravatar.com/avatar/211380370e2d82a89eb157a46f397510.jpg?s=200&d=mm">
                                <span v-if="form.emails.length > 1">{{ email }}</span>
                                <div v-else class="d-flex flex-column">
                                    <span>{{ email }}</span>
                                    <multiselect
                                        v-model="form.teams"
                                        id="userTeamsList"
                                        placeholder="Select Teams..."
                                        :options="userTeams"
                                        :multiple="true"
                                        track-by="id"
                                        label="name"
                                        :taggable="true"
                                        :close-on-select="false">
                                    </multiselect>
                                </div>
                            </div>

                            <label class="custom-checkbox my-3 justify-content-center">
                                <input type="checkbox" v-model="form.dontSendEmailToUser"/>
                                <span class="checkmark"></span>
                                Don't send an email invite when this user is added to FollowupCRM. They'll still get access to this account once they log in.
                            </label>

                            <h4 class="mb-4 mt-2 text-followup-nav">Permissions</h4>
                            <div>
                                <div
                                    v-if="form.presetAccessType !== 'system-admin'"
                                    v-for="(actions, section) in form.permissions"
                                    :key="section"
                                    class="mb-6">
                                    <strong class="text-followup-nav capitalize mb-2">{{ formatSimpleLabel(section) }}</strong>
                                    <div
                                        v-for="(accessLevel, action) in actions"
                                        :key="action"
                                        class="text-base">
                                        <strong class="capitalize">{{ action }}</strong> ( {{ accessLevel.length > 0 ? formattedAccessLabels(accessLevel) : 'None' }} )
                                    </div>
                                </div>
                                <div v-if="form.presetAccessType === 'system-admin'">All system admin permissions</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer bg-white p-2 justify-content-center">
                    <button type="button" class="btn btn-outline-secondary mx-2" @click="close">Cancel</button>
                    <button v-if="currentStep > 1 && currentStep <= 3" type="button" class="btn btn-primary mx-2" @click="goBack">Back</button>
                    <button v-if="currentStep < 3" type="button" class="btn btn-primary mx-2" @click="goNext">Next</button>
                    <button v-if="currentStep === 3" type="button" class="btn btn-primary mx-2" @click="createUsers">Create</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import Papa from 'papaparse';
import ChoosePermissions from "../preset/choose-permissions.vue";
import ChooseSetAccess from "../preset/choose-set-access.vue";
import Multiselect from "vue-multiselect";

export default {
    name: 'create-user-modal',
    components: {Multiselect, ChooseSetAccess, ChoosePermissions},
    props: ['team'],
    data() {
        return {
            steps: [
                { name: 'Email', stepCount: 1 },
                { name: 'Access', stepCount: 2 },
                { name: 'Review', stepCount: 3 }
            ],
            currentStep: 1,
            form: {
                teams: [],
                emails: [],
                permissions: [],
                selectedPreset: null,
                presetAccessType: null,
                dontSendEmailToUser: false
            },
            userTeams: []
        }
    },
    mounted() {
        this.getUserAllTeams();
    },
    methods: {
        open() {
            $("#createUserModal").modal("show");
        },
        close() {
            this.resetFormData();
            $("#createUserModal").modal("hide");
        },
        goNext() {
            if(this.currentStep === 1 && this.form.emails.length === 0) {
                this.getToast().fire({
                    type: 'error',
                    title: 'Please enter email address(es)'
                });
                return false;
            }

            if(this.currentStep === 2) {
                if(this.$refs.ChooseSetAccess.selectedPresetAccessType !== 'system-admin') {
                    if(this.$refs.ChooseSetAccess.selectedPresetAccessType === 'template' && this.$refs.ChooseSetAccess.selectedPreset === null) {
                        this.getToast().fire({
                            type: 'error',
                            title: 'Please select a preset template'
                        });
                        return false;
                    }
                    this.form.permissions = this.$refs.ChooseSetAccess.$refs.ChoosePermissions.selectedPermissions;
                }
                else {
                    this.form.permissions = [];
                }
                this.form.presetAccessType = this.$refs.ChooseSetAccess.selectedPresetAccessType;
            }

            this.currentStep += 1;
        },
        goBack() {
            this.currentStep -= 1;
        },
        createUsers() {
            let self = this;

            this.form.selectedPreset = this.$refs.ChooseSetAccess.selectedPreset;
            this.form.presetAccessType = this.$refs.ChooseSetAccess.selectedPresetAccessType;
            this.form.permissions = this.form.presetAccessType !== 'system-admin' ? this.$refs.ChooseSetAccess.$refs.ChoosePermissions.selectedPermissions : [];

            axios.post('/settings/'+this.team.id+'/members/register-multiple-users', this.form).then(({data}) => {
                this.$emit('users-created');
                this.resetFormData();
                this.close();
                this.getToast().fire({
                    type: 'success',
                    title: 'Users created successfully.'
                });
            }).catch((error) => {
                if(error.response.status === 422) {
                    this.getToast(6000).fire({
                        type: 'error',
                        title: Object.keys(error.response.data.errors).join(', ') + ' emails has already been taken'
                    });
                }
            });
        },
        validateEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if(file) {
                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: this.extractEmails
                });
            }
        },
        extractEmails(results) {
            const rows = results.data;
            const emailList = [];

            rows.forEach(row => {
                for(let key in row) {
                    if(key.toLowerCase() === 'email') {
                        const email = row[key]?.trim();
                        if(this.validateEmail(email)) {
                            emailList.push(email);
                        }
                    }
                }
            });

            this.form.emails = [...new Set([...this.form.emails, ...emailList])];
        },
        resetFormData() {
            this.currentStep = 1;
            this.form.teams = [];
            this.form.emails = [];
            this.form.permissions = [];
            this.form.selectedPreset = null;
            this.form.presetAccessType = null;
            this.form.dontSendEmailToUser = null;
            this.$refs.ChooseSetAccess.resetAllData();
        },
        formattedAccessLabels(accessLevel) {
            if(this.checkArrayType(accessLevel) === 'objects') {
                return accessLevel.map(obj => obj.label).join(', ');
            }
            else {
                return accessLevel.map(this.formatSimpleLabel).join(', ');
            }
        },
        formatSimpleLabel(label) {
            if(label === 'assigned-those-reports') {
                return 'Assigned to those who report to them';
            }
            if(label === 'erp') {
                return 'ERP';
            }
            if(label === 'erp-integration-tab') {
                return 'ERP Integration';
            }
            if(label === 'company-setting') {
                return 'Company Settings';
            }
            if(label.endsWith('-tab')) {
                label = label.slice(0, -4);
            }

            return label.split('-')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        },
        checkArrayType(arr) {
            if(arr.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) {
                return 'objects';
            }
            if(arr.every(item => typeof item !== 'object' || item === null)) {
                return 'simple';
            }
            return 'none';
        },
        getUserAllTeams() {
            let self = this;

            axios.get('/settings/'+this.team.id+'/members/get-all-teams').then(({data}) => {
                self.userTeams = data.data;
            });
        }
    }
}
</script>

<style scoped>
.stepper-wrapper {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    width: 80%;
}
.stepper-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    @media (max-width: 768px) {
        font-size: 12px;
    }
}
.stepper-item::before {
    position: absolute;
    content: "";
    border-bottom: 2px solid #ccc;
    width: 100%;
    top: 9px;
    left: -50%;
    z-index: 2;
}
.stepper-item::after {
    position: absolute;
    content: "";
    border-bottom: 2px solid #ccc;
    width: 100%;
    top: 9px;
    left: 50%;
    z-index: 2;
}
.stepper-item .step-counter {
    position: relative;
    z-index: 5;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #ccc;
    margin-bottom: 6px;
}
.stepper-item.active {
    font-weight: bold;
}
.stepper-item.active .step-name {
    color: #3d4852;
}
.stepper-item.completed .step-counter {
    background-color: #01b1af;
}
.stepper-item.completed .step-name {
    color: #01b1af;
}
.stepper-item.completed::after {
    position: absolute;
    content: "";
    border-bottom: 2px solid #01b1af;
    width: 100%;
    top: 9px;
    left: 50%;
    z-index: 3;
}
.stepper-item:first-child::before {
    content: none;
}
.stepper-item:last-child::after {
    content: none;
}
.step-counter-text {
    font-size: 15px;
    font-weight: 600;
}
.csv-upload-label {
    display: inline-block;
    width: 250px;
    border: 1px solid #01b1af;
    border-radius: 8px;
    padding: 30px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.3s ease;
}
.csv-upload-label:hover {
    border-color: #01b1af;
}
.csv-upload-label img {
    width: 60px;
    margin-bottom: 20px;
}
.csv-upload-label h6 {
    font-weight: bold;
    color: #10b2aa;
}
.csv-upload-label small {
    color: #6c757d;
}
.csv-upload-input {
    display: none;
}

.section-divider {
    border-top: 1px solid #ddd;
    margin: 60px 0;
}

h4 {
    font-weight: 600;
}
.modal-fullscreen-custom {
    height: 100vh;
    display: flex;
    flex-direction: column;
    margin: 0;
    border: none;
    border-radius: 0;
    max-width: 100% !important;
}
.modal-fullscreen-custom .modal-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1051;
    height: 62px;
}
.modal-fullscreen-custom .modal-footer {
    position: fixed;
    bottom: 5px;
    left: 0;
    right: 0;
    z-index: 1051;
    height: 62px;
}
.modal-fullscreen-custom .modal-scrollable {
    overflow-y: auto;
    flex: 1 1 auto;
    margin-top: 70px;
    margin-bottom: 70px;
    padding: 15px;
}
.modal-fullscreen-custom .modal-content {
    height: 100vh;
    display: flex;
    flex-direction: column;
}
.modal-fullscreen-custom .modal-header,
.modal-fullscreen-custom .modal-footer {
    flex-shrink: 0;
}
.modal-fullscreen-custom .modal-body {
    flex: 1 1 auto;
    overflow-y: auto;
}
.user-icon {
    width: 40px;
    height: 40px;
    background-color: #e9eef3;
    border-radius: 50%;
    display: inline-block;
    margin-right: 10px;
}
.custom-checkbox {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    user-select: none;
    gap: 8px;
}
.custom-checkbox input[type="checkbox"] {
    display: none;
}
.custom-checkbox .checkmark {
    width: 21px;
    height: 21px;
    background-color: #fff;
    border: 2px solid #01B1AF;
    border-radius: 4px;
    display: inline-block;
    position: relative;
    transition: background-color 0.3s, border-color 0.3s;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark {
    background-color: #01B1AF;
    border-color: #01B1AF;
}
.custom-checkbox input[type="checkbox"]:checked + .checkmark::after {
    content: '';
    position: absolute;
    top: 45%;
    left: 50%;
    width: 7px;
    height: 14px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: translate(-50%, -50%) rotate(45deg);
}
</style>

<style>
.email-custom-tag-input .b-form-tag {
    background-color: #01B1AF !important;
    color: #fff !important;
    border-radius: 3px !important;
    align-items: center !important;
    margin-top: 5px !important;
    margin-left: 5px !important;
    font-size: 14px !important;
    padding: 5px 7px !important;
}
.email-custom-tag-input .b-form-tag .close {
    font-size: 1.1rem !important;
    font-weight: 400 !important;
    line-height: unset !important;
    color: #000 !important;
    margin-left: 5px !important;
}
</style>
